import { useState } from "react";
import { ChevronDown, ChevronRight, Download, Move } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUiStore } from "../stores/ui";
import type { WorkStepRow } from "../api/queries/work_orders";
import { workOrdersApi, workStepsApi } from "../api/queries/work_orders";
import { devicesApi } from "../api/queries/devices";
import { toast } from "./Toast";
import { StatusDot, workStepTone } from "./StatusDot";
import { formatDuration } from "../lib/time";

export interface WorkOrderTimelineProps {
  steps: WorkStepRow[];
}

export function WorkOrderTimeline({ steps }: WorkOrderTimelineProps) {
  const [transferringStep, setTransferringStep] = useState<WorkStepRow | null>(
    null,
  );
  if (steps.length === 0) {
    return (
      <div className="card px-6 py-8 text-center text-secondary">
        No steps yet. The CEO is still planning.
      </div>
    );
  }
  return (
    <>
      <ol className="flex flex-col gap-3">
        {steps.map((s) => (
          <TimelineStep
            key={s.id}
            step={s}
            onTransfer={() => setTransferringStep(s)}
          />
        ))}
      </ol>
      {transferringStep && (
        <TransferStepModal
          step={transferringStep}
          onClose={() => setTransferringStep(null)}
        />
      )}
    </>
  );
}

function TimelineStep({
  step,
  onTransfer,
}: {
  step: WorkStepRow;
  onTransfer: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const tone = workStepTone(step.status);
  const duration =
    step.started_at && step.ended_at ? formatDuration(step.ended_at - step.started_at) : null;
  // Transfer is meaningful only while the step is in flight on a device.
  const canTransfer =
    !!step.device_id &&
    (step.status === "running" || step.status === "queued");

  return (
    <li className="card overflow-hidden">
      <button
        type="button"
        className="flex w-full items-start gap-3 p-3 text-left hover:bg-raised"
        onClick={() => setExpanded((x) => !x)}
        aria-expanded={expanded}
      >
        <span className="mt-1">
          <StatusDot tone={tone} pulse={tone === "running"} size="lg" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[13px] font-semibold text-primary">{step.agent_role}</span>
            <span className="text-[12px] text-tertiary">on</span>
            <span className="text-[12px] font-mono text-secondary">
              {step.device_name ?? step.device_id ?? "unassigned"}
            </span>
            <span
              className={`badge ml-auto badge--${
                tone === "running" ? "running" : tone === "error" ? "error" : tone === "blocked" ? "blocked" : "idle"
              }`}
            >
              {step.status}
            </span>
          </div>
          <div
            className={`mt-1 font-mono text-[12px] text-secondary leading-[18px] ${
              expanded ? "" : "line-clamp-2"
            }`}
          >
            {step.instruction}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-tertiary">
            {duration && <span>took {duration}</span>}
            {expanded ? (
              <ChevronDown size={12} aria-hidden="true" />
            ) : (
              <ChevronRight size={12} aria-hidden="true" />
            )}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border-subtle p-3">
          {/* Transfer linkage callouts so the user can navigate the lineage. */}
          {step.resumed_from_step_id && (
            <div className="mb-3 rounded-sm border border-border-default bg-raised p-2 text-[12px] text-secondary">
              Resumed from step{" "}
              <span className="font-mono text-tertiary">{step.resumed_from_step_id}</span>
              {step.checkpoint_blob_id && (
                <>
                  {" "}
                  (checkpoint{" "}
                  <span className="font-mono text-tertiary">
                    {step.checkpoint_blob_id}
                  </span>
                  )
                </>
              )}
              .
            </div>
          )}
          {step.transferred_to_step_id && (
            <div className="mb-3 rounded-sm border border-border-default bg-raised p-2 text-[12px] text-secondary">
              Transferred to step{" "}
              <span className="font-mono text-tertiary">{step.transferred_to_step_id}</span>{" "}
              — work continued there.
            </div>
          )}
          {step.result?.error && (
            <div className="mb-3 rounded-sm border border-status-error/40 bg-status-error/10 p-2 text-[12px] text-status-error">
              <div className="font-mono text-[11px] uppercase">{step.result.error.code}</div>
              <div>{step.result.error.message}</div>
            </div>
          )}
          {step.result?.outputs && step.result.outputs.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {step.result.outputs.map((o, i) => (
                <li key={i}>
                  <OutputView output={o} />
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[12px] text-tertiary">No outputs yet.</div>
          )}
          {canTransfer && (
            <div className="mt-3 flex justify-end">
              <button
                className="btn btn--secondary btn--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onTransfer();
                }}
                type="button"
                title="Move this work to a different computer"
              >
                <Move size={12} />
                Transfer to another computer…
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Modal for moving a running step to a different device. Lists the company's
 * online devices (excluding the source) and POSTs to /v1/work-steps/:id/transfer
 * on confirm. The HTTP turn blocks until the source-side checkpoint completes
 * (default cap 30 s) so we surface a clear progress state to the user.
 */
function TransferStepModal({
  step,
  onClose,
}: {
  step: WorkStepRow;
  onClose: () => void;
}) {
  const companyId = useUiStore((s) => s.activeCompanyId);
  const qc = useQueryClient();

  const devicesQ = useQuery({
    queryKey: ["devices", companyId],
    queryFn: () => devicesApi.list(companyId!),
    enabled: !!companyId,
  });

  const candidates = (devicesQ.data?.devices ?? []).filter(
    (d) => d.id !== step.device_id && d.online && d.pairing_state === "active",
  );

  const [target, setTarget] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  const transfer = useMutation({
    mutationFn: () =>
      workStepsApi.transfer(step.id, target, reason.trim() || undefined),
    onSuccess: (res) => {
      toast(
        `Moved to new step ${res.new_work_step_id.slice(0, 8)}…`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["work-order", step.work_order_id] });
      onClose();
    },
    onError: (err: Error) =>
      toast(err.message ?? "Transfer failed", "error"),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-modal-title"
      className="fixed inset-0 z-50 grid place-items-center bg-canvas/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !transfer.isPending) onClose();
      }}
    >
      <div className="card w-[460px] max-w-[92vw] p-5">
        <h2 id="transfer-modal-title" className="m-0 mb-1 text-[16px] font-semibold">
          Transfer step to another computer
        </h2>
        <p className="text-[12px] text-tertiary">
          The current device's progress will be checkpointed (issue body, comments,
          and work products) and the new device will resume from that point.
        </p>
        <div className="mt-3 rounded-sm bg-raised p-3 text-[12px]">
          <div className="micro mb-1">Currently on</div>
          <div className="font-mono">
            {step.device_name ?? step.device_id ?? "unassigned"}
          </div>
          <div className="micro mt-2 mb-1">Step</div>
          <div className="font-mono text-tertiary">{step.id}</div>
          <div className="line-clamp-2 mt-1 text-secondary">{step.instruction}</div>
        </div>
        <label className="field mt-4">
          <span className="field__label">Target computer</span>
          <select
            className="input"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={transfer.isPending}
          >
            <option value="">— pick one —</option>
            {candidates.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          {candidates.length === 0 && (
            <span className="field__error">
              No other online devices in this company.
            </span>
          )}
        </label>
        <label className="field mt-3">
          <span className="field__label">Reason (optional)</span>
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., laptop battery low"
            disabled={transfer.isPending}
            maxLength={500}
          />
        </label>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            className="btn btn--secondary btn--sm"
            onClick={onClose}
            type="button"
            disabled={transfer.isPending}
          >
            Cancel
          </button>
          <button
            className="btn btn--primary btn--sm"
            onClick={() => transfer.mutate()}
            type="button"
            disabled={!target || transfer.isPending}
          >
            {transfer.isPending ? "Transferring…" : "Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}

type StepOutput = NonNullable<NonNullable<WorkStepRow["result"]>["outputs"]>[number];

function OutputView({ output }: { output: StepOutput }) {
  if (output.kind === "text") {
    return (
      <div>
        <div className="micro mb-1">{output.name}</div>
        <pre className="bg-sunken border border-border-subtle rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap text-secondary max-h-[220px] overflow-auto">
          {output.text}
        </pre>
      </div>
    );
  }
  if (output.kind === "inline") {
    return (
      <div>
        <div className="micro mb-1">
          {output.name} <span className="text-tertiary">· {output.mime}</span>
        </div>
        <pre className="bg-sunken border border-border-subtle rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap text-secondary max-h-[160px] overflow-auto">
          {atobSafe(output.data)}
        </pre>
      </div>
    );
  }
  return (
    <a
      href={workOrdersApi.blobUrl(output.blob_id)}
      className="card flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-raised"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Download size={14} className="text-secondary" aria-hidden="true" />
      <span className="font-mono text-primary">{output.name}</span>
      <span className="text-tertiary">· {output.mime}</span>
      <span className="ml-auto font-mono text-tertiary">{formatBytes(output.bytes)}</span>
    </a>
  );
}

function atobSafe(s: string): string {
  try {
    return atob(s);
  } catch {
    return s;
  }
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / (1024 * 1024)).toFixed(2)}MB`;
}
